import DyteClient from '@dytesdk/web-core';
import React, { useContext, useEffect, useState } from 'react';
import { States } from '../types/props';
import { DyteI18n, useLanguage } from '../lib/lang';
import { defaultIconPack, IconPack } from '../lib/icons';
import { UIConfig } from '../types/ui-config';

import { useDyteSelector } from '@dytesdk/react-native-core';
import DyteText from './DyteText';
import { StyleSheet, View } from 'react-native';
import { defaultConfig } from '../lib/default-ui-config';
import DyteMixedGrid from './DyteMixedGrid';
import DyteLiveStreamPlayer from './DyteLiveStreamPlayer';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function DyteGrid({
  meeting,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config = defaultConfig,
  states,
  t,
  aspectRatio = '3:4',
  gap = 8,
  iconPack = defaultIconPack,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size = 'sm',
}: {
  meeting: DyteClient;
  config?: UIConfig;
  states?: States;
  t?: DyteI18n;
  aspectRatio?: string;
  gap?: number;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
}) {
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates, setStates } = useContext(DyteUIContext);
  states = states ?? storeStates;
  const { colors } = storeStates.designSystem;
  const pageCount = useDyteSelector((m) => m.participants.pageCount);
  const currentPage = useDyteSelector((m) => m.participants.currentPage);
  const livestream = useDyteSelector((m) => m.livestream);
  const self = useDyteSelector((m) => m.self);
  const isSelfPinned = self.isPinned;
  const shouldAddSelfParticipant = () => {
    if (meeting.meta.viewType === 'GROUP_CALL') {
      if (meeting.participants.viewMode === 'ACTIVE_GRID') return true;
      else {
        if (currentPage === pageCount) return true;
        else return false;
      }
    } else {
      if (
        meeting.self.stageStatus === 'ON_STAGE' &&
        meeting.participants.viewMode === 'ACTIVE_GRID'
      )
        return true;
      else if (
        meeting.self.stageStatus === 'ON_STAGE' &&
        meeting.participants.viewMode !== 'ACTIVE_GRID'
      ) {
        if (currentPage === pageCount) return true;
        else false;
      } else return false;
    }
    return false;
  };
  const participants = useDyteSelector((m) => m.participants);
  const [activeParticipants, setActiveParticipants] = useState(
    participants.active.toArray()
  );
  const [pinnedParticipants, setPinnedParticipants] = useState(
    participants.pinned.toArray()
  );
  const [screenshareParticipants, setScreenshareParticipants] = useState(
    activeParticipants.filter((p) => p.screenShareEnabled)
  );
  const sharingScreen = useDyteSelector((m) => m.self.screenShareEnabled);
  const activePlugins = useDyteSelector((m) => m.plugins.active);
  const selfWebinarStatus = useDyteSelector((m) => m.stage.status);
  const onStage = activeParticipants.filter(
    (p) => p.stageStatus === 'ON_STAGE'
  );
  useEffect(() => {
    const handleActiveParticipantUpdate = () => {
      setActiveParticipants(participants.active.toArray());
    };
    const addPinnedParticipantUpdate = (p) => {
      setPinnedParticipants((_pinnedP) => {
        const exists = _pinnedP.some((el) => el.userId === p.userId);
        if (!exists) return [..._pinnedP, p];
        else return _pinnedP;
      });
    };
    const removePinnedParticipantUpdate = (p) => {
      setPinnedParticipants((_pinnedP) => {
        return _pinnedP.filter((currentP) => currentP.userId !== p.userId);
      });
    };
    const participantStageUpdateHandler = (p) => {
      handleActiveParticipantUpdate();
      if (p.isPinned) removePinnedParticipantUpdate(p);
    };
    const selfStageUpdateHandler = () => {
      handleActiveParticipantUpdate();
      removePinnedParticipantUpdate(meeting.self);
    };
    const participantScreenshareUpdateHandler = () => {
      const update = activeParticipants.filter((p) => p.screenShareEnabled);
      setScreenshareParticipants(update);
    };
    meeting.participants.active.on(
      'participantsUpdate',
      handleActiveParticipantUpdate
    );
    meeting.participants.joined.on('pinned', addPinnedParticipantUpdate);
    meeting.participants.joined.on('unpinned', removePinnedParticipantUpdate);
    meeting.participants.joined.on(
      'participantLeft',
      removePinnedParticipantUpdate
    );
    meeting.participants.on('viewModeChanged', handleActiveParticipantUpdate);
    meeting.participants.on('pageChanged', handleActiveParticipantUpdate);
    meeting.participants.active.on(
      'stageStatusUpdate',
      participantStageUpdateHandler
    );
    meeting.stage.on('stageStatusUpdate', selfStageUpdateHandler);
    meeting.participants.active.on(
      'screenShareUpdate',
      participantScreenshareUpdateHandler
    );
    return () => {
      meeting.participants.joined.off('pinned', addPinnedParticipantUpdate);
      meeting.participants.joined.off(
        'unpinned',
        removePinnedParticipantUpdate
      );
      meeting.participants.joined.off(
        'participantLeft',
        removePinnedParticipantUpdate
      );
      meeting.participants.active.off(
        'participantsUpdate',
        handleActiveParticipantUpdate
      );
      meeting.participants.off(
        'viewModeChanged',
        handleActiveParticipantUpdate
      );
      meeting.participants.off('pageChanged', handleActiveParticipantUpdate);
      meeting.participants.active.off(
        'stageStatusUpdate',
        participantStageUpdateHandler
      );
      meeting.stage.off('stageStatusUpdate', selfStageUpdateHandler);
      meeting.participants.active.off(
        'screenShareUpdate',
        participantScreenshareUpdateHandler
      );
    };
  }, [activeParticipants, meeting, participants]);
  const handleTouch = () => {
    setStates({
      ...states,
      activeMoreMenu: false,
    });
  };
  const styles = StyleSheet.create({
    banner: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.text,
      backgroundColor: colors.background[900],
    },
  });

  // Livestream not started
  if (
    meeting.meta.viewType === 'LIVESTREAM' &&
    self.stageStatus !== 'ON_STAGE' &&
    livestream?.state === 'IDLE'
  )
    return (
      <View style={styles.banner}>
        <DyteText size="md">{t('livestream.idle')}</DyteText>
      </View>
    );

  // Livestream Grid
  if (
    meeting.meta.viewType === 'LIVESTREAM' &&
    self.stageStatus !== 'OFF_STAGE'
  )
    return (
      <DyteMixedGrid
        meeting={meeting}
        participants={
          shouldAddSelfParticipant() ? [...onStage, meeting.self] : onStage
        }
        pinnedParticipants={
          isSelfPinned
            ? [...pinnedParticipants, meeting.self]
            : pinnedParticipants
        }
        screenShareParticipants={
          sharingScreen
            ? [...screenshareParticipants, meeting.self]
            : screenshareParticipants
        }
        plugins={activePlugins.toArray()}
        aspectRatio={aspectRatio}
        iconPack={iconPack}
        gap={gap}
        t={t}
      />
    );

  // Livestream Player
  if (
    meeting.meta.viewType === 'LIVESTREAM' &&
    self.stageStatus === 'OFF_STAGE' &&
    livestream?.state === 'LIVESTREAMING'
  )
    return <DyteLiveStreamPlayer />;

  // Webinar Grid
  if (
    meeting.meta.viewType === 'WEBINAR' &&
    (onStage.length > 0 ||
      selfWebinarStatus === 'ON_STAGE' ||
      selfWebinarStatus === 'ACCEPTED_TO_JOIN_STAGE')
  )
    return (
      // eslint-disable-next-line react-native/no-inline-styles
      <View onTouchStart={handleTouch} style={{ flex: 1 }}>
        <DyteMixedGrid
          meeting={meeting}
          participants={
            shouldAddSelfParticipant() ? [...onStage, meeting.self] : onStage
          }
          pinnedParticipants={
            isSelfPinned
              ? [...pinnedParticipants, meeting.self]
              : pinnedParticipants
          }
          screenShareParticipants={
            sharingScreen
              ? [...screenshareParticipants, meeting.self]
              : screenshareParticipants
          }
          plugins={activePlugins.toArray()}
          aspectRatio={aspectRatio}
          iconPack={iconPack}
          gap={gap}
          t={t}
        />
      </View>
    );

  // Webinar not started
  if (meeting.meta.viewType === 'WEBINAR' && onStage.length === 0)
    return (
      <View style={styles.banner}>
        <DyteText size="md">{t('stage.empty_host')}</DyteText>
      </View>
    );
  // Simple Group Call
  return (
    // eslint-disable-next-line react-native/no-inline-styles
    <View onTouchStart={handleTouch} style={{ flex: 1 }}>
      <DyteMixedGrid
        meeting={meeting}
        participants={
          shouldAddSelfParticipant()
            ? [...activeParticipants, meeting.self]
            : activeParticipants
        }
        pinnedParticipants={
          isSelfPinned
            ? [...pinnedParticipants, meeting.self]
            : pinnedParticipants
        }
        screenShareParticipants={
          sharingScreen
            ? [...screenshareParticipants, meeting.self]
            : screenshareParticipants
        }
        plugins={activePlugins.toArray()}
        aspectRatio={aspectRatio}
        iconPack={iconPack}
        gap={gap}
        t={t}
      />
    </View>
  );
}
